import classNames from "classnames";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: classNames.ArgumentArray) {
  return twMerge(classNames(inputs));
}

/**
 * 复制文字
 * @param {string} text  要复制的文本
 * @returns {boolean} true/false
 */
export const copyText = async (text: string) => {
  if (window.navigator?.clipboard) {
    window.navigator.clipboard.writeText(text)
    return true
  }
  // 动态创建 textarea 标签
  const textarea = document.createElement('textarea')
  // 将该 textarea 设为 readonly 防止 iOS 下自动唤起键盘，同时将 textarea 移出可视区域
  textarea.readOnly = true
  textarea.style.position = 'absolute'
  textarea.style.left = '-9999px'
  textarea.style.opacity = '0'
  // 将要 copy 的值赋给 textarea 标签的 value 属性
  textarea.value = text
  // 将 textarea 插入到 body 中
  document.body.appendChild(textarea)
  // 选中值并复制
  textarea.select()
  const result = document.execCommand('Copy')
  document.body.removeChild(textarea)
  return result
}

/**
 * 结构化更新 - 深度合并对象，支持嵌套对象和数组的更新
 * @param prev 原始数据对象
 * @param updates 要应用的更新数据
 * @returns 更新后的新对象（不修改原对象）
 */

export function structuralUpdate<T extends Record<string, any>>(
  prev: T,
  updates: Record<string, any> | null | undefined
): T {
  // 如果更新数据为空或未定义，返回原对象
  if (updates === null || updates === undefined) {
    return prev;
  }

  // 如果更新数据不是对象类型，直接返回更新数据
  if (typeof updates !== 'object') {
    return updates as T;
  }

  // 如果原数据不是对象，返回更新数据
  if (typeof prev !== 'object' || prev === null) {
    return updates as T;
  }

  // 处理数组情况
  if (Array.isArray(prev)) {
    if (Array.isArray(updates)) {
      // 如果都是数组，进行数组合并
      const result = [...prev];
      updates.forEach((item, index) => {
        if (index < result.length) {
          result[index] = structuralUpdate(result[index], item);
        } else {
          result.push(item);
        }
      });
      return result as unknown as T;
    } else {
      // 如果原数据是数组但更新数据不是，返回更新数据
      return updates as T;
    }
  }

  // 处理对象情况
  const result = { ...prev } as Record<string, any>;

  // 遍历更新对象的所有属性
  for (const [key, value] of Object.entries(updates)) {
    if (value === null || value === undefined) {
      // 如果值为 null 或 undefined，删除该属性
      delete result[key];
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // 如果是普通对象，递归合并
      if (prev[key] && typeof prev[key] === 'object' && !Array.isArray(prev[key])) {
        result[key] = structuralUpdate(prev[key], value);
      } else {
        result[key] = value;
      }
    } else if (Array.isArray(value)) {
      // 如果是数组，侧全量更新
      if (Array.isArray(prev[key])) {
        result[key] = value;
        // result[key] = structuralUpdate(prev[key], value);
      } else {
        result[key] = value;
      }
    } else {
      // 基础类型，直接赋值
      result[key] = value;
    }
  }

  return result as T;
}

/**
 * 深度克隆对象
 * @param obj 要克隆的对象
 * @returns 克隆后的新对象
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as T;
  }

  if (obj instanceof Array) {
    return obj.map(item => deepClone(item)) as T;
  }

  if (typeof obj === 'object') {
    const clonedObj = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }

  return obj;
}

/**
 * 检查两个对象是否深度相等
 * @param obj1 第一个对象
 * @param obj2 第二个对象
 * @returns 是否相等
 */
export function deepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) {
    return true;
  }

  if (obj1 == null || obj2 == null) {
    return obj1 === obj2;
  }

  if (typeof obj1 !== typeof obj2) {
    return false;
  }

  if (typeof obj1 !== 'object') {
    return obj1 === obj2;
  }

  if (Array.isArray(obj1) !== Array.isArray(obj2)) {
    return false;
  }

  if (Array.isArray(obj1)) {
    if (obj1.length !== obj2.length) {
      return false;
    }
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) {
        return false;
      }
    }
    return true;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key)) {
      return false;
    }
    if (!deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

/**
 * 获取对象的嵌套属性值
 * @param obj 目标对象
 * @param path 属性路径，如 'a.b.c' 或 ['a', 'b', 'c']
 * @param defaultValue 默认值
 * @returns 属性值或默认值
 */
export function getNestedValue<T = any>(
  obj: any,
  path: string | string[],
  defaultValue?: T
): T | undefined {
  const keys = Array.isArray(path) ? path : path.split('.');
  let current = obj;

  for (const key of keys) {
    if (current == null || typeof current !== 'object') {
      return defaultValue;
    }
    current = current[key];
  }

  return current !== undefined ? current : defaultValue;
}

/**
 * 设置对象的嵌套属性值
 * @param obj 目标对象
 * @param path 属性路径，如 'a.b.c' 或 ['a', 'b', 'c']
 * @param value 要设置的值
 * @returns 更新后的对象
 */
export function setNestedValue<T extends Record<string, any>>(
  obj: T,
  path: string | string[],
  value: any
): T {
  const keys = Array.isArray(path) ? path : path.split('.');
  const result = deepClone(obj);
  let current: any = result;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (current[key] == null || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
  return result;
}